/**
 * Official telegram-web-app.js implements WebApp.requestContact(callback), not Promise.
 * The callback receives (sent, webViewEvent); phone data is in webViewEvent.response / responseUnsafe.
 * @see https://core.telegram.org/bots/webapps
 */

export type TelegramContactRequestHost = {
  requestContact: (
    callback: (sent: boolean, webViewEvent?: unknown) => void
  ) => unknown;
};
export function parseTelegramRequestedContactPhone(
  webViewEvent: unknown
): string {
  if (!webViewEvent || typeof webViewEvent !== "object") return "";
  const ev = webViewEvent as Record<string, unknown>;
  const unsafe = ev.responseUnsafe;
  if (unsafe && typeof unsafe === "object") {
    const u = unsafe as Record<string, unknown>;
    const c = u.contact;
    if (c && typeof c === "object") {
      const contact = c as Record<string, unknown>;
      const p = contact.phone_number ?? contact.phone;
      if (typeof p === "string" && p.length) return normalizePhone(p);
    }
    const direct = u.phone_number ?? u.phone;
    if (typeof direct === "string" && direct.length) return normalizePhone(direct);
  }
  const raw = ev.response;
  if (typeof raw !== "string" || !raw.length) return "";
  try {
    const params = new URLSearchParams(raw);
    const fromQs = params.get("phone_number") || params.get("phone") || "";
    if (fromQs) return normalizePhone(fromQs);
  } catch {
    /* ignore */
  }
  try {
    const trimmed = raw.trim();
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>;
      const contact = parsed.contact as Record<string, unknown> | undefined;
      const p =
        (typeof parsed.phone_number === "string" && parsed.phone_number) ||
        (typeof parsed.phone === "string" && parsed.phone) ||
        (contact &&
          typeof contact === "object" &&
          typeof contact.phone_number === "string" &&
          contact.phone_number) ||
        (contact &&
          typeof contact === "object" &&
          typeof contact.phone === "string" &&
          contact.phone) ||
        "";
      if (typeof p === "string" && p.length) return normalizePhone(p);
    }
  } catch {
    /* ignore */
  }

  return "";
}

function normalizePhone(phone: string): string {
  return phone.replace(/[^\d+]/g, "");
}

export function requestTelegramContactPhone(
  tg: TelegramContactRequestHost,
  timeoutMs = 30000
): Promise<string> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timeoutId = window.setTimeout(() => {
      if (!settled) {
        settled = true;
        reject(new Error("Timeout waiting for phone number"));
      }
    }, timeoutMs);

    const finishOk = (phone: string) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(phone);
    };

    const finishErr = (err: Error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      reject(err);
    };

    try {
      const maybePromise = tg.requestContact(
        (sent: boolean, webViewEvent?: unknown) => {
          if (!sent) {
            finishErr(new Error("User declined"));
            return;
          }
          const phone = parseTelegramRequestedContactPhone(webViewEvent);
          const digits = phone.replace(/\D/g, "");
          if (digits.length >= 10) finishOk(phone);
          else finishErr(new Error("No phone number received"));
        }
      );

      if (
        maybePromise &&
        typeof (maybePromise as Promise<unknown>).then === "function"
      ) {
        (maybePromise as Promise<unknown>)
          .then((data: unknown) => {
            let phone = "";
            if (typeof data === "string") phone = normalizePhone(data);
            else if (data && typeof data === "object") {
              const o = data as Record<string, unknown>;
              const c = o.contact as Record<string, unknown> | undefined;
              phone = normalizePhone(
                String(
                  o.phone_number ??
                    o.phone ??
                    c?.phone_number ??
                    c?.phone ??
                    ""
                )
              );
            }
            const digits = phone.replace(/\D/g, "");
            if (digits.length >= 10) finishOk(phone);
            else finishErr(new Error("No phone number received"));
          })
          .catch((err: unknown) => {
            finishErr(
              err instanceof Error ? err : new Error(String(err))
            );
          });
      }
    } catch (e) {
      finishErr(e instanceof Error ? e : new Error(String(e)));
    }
  });
}
